import React, { useState, useEffect } from 'react';
import { CHAINS, getPopularChains } from '../config/chains';

const TokenSelector = ({ 
    isOpen, 
    onClose, 
    onTokenSelect,
    selectedChainId = null,
    selectedTokenId = null,
    title = "Select token to swap",
    showConfidential = false
}) => {
    const [tokenSearchTerm, setTokenSearchTerm] = useState('');
    const [selectedChain, setSelectedChain] = useState(selectedChainId);
    const [selectedToken, setSelectedToken] = useState(selectedTokenId);

    const popularChains = React.useMemo(() => getPopularChains(), []);
    const singleChainMode = popularChains.length === 1;
    const soleChainId = singleChainMode ? popularChains[0]?.id : null;

    // Helper function to check if a token address is valid (EVM 0x or Soroban contract strkey)
    const isValidTokenAddress = (address) => {
        if (!address) return false;
        const raw = address.trim();
        const addr = raw.toLowerCase();
        if (addr === '' || addr === '0x0000000000000000000000000000000000000000') return false;
        if (/^0x[a-fA-F0-9]{40}$/.test(addr)) return true;
        // Soroban contract id (strkey: C + 55 chars; Stellar base32 is A–Z234567, incl. I/O)
        const upper = raw.toUpperCase();
        return /^C[ABCDEFGHIJKLMNOPQRSTUVWXYZ234567]{55}$/.test(upper);
    };

    // Get tokens to display for the selected chain.
    const tokensToDisplay = selectedChain && CHAINS[selectedChain] 
        ? Object.entries(CHAINS[selectedChain].tokens)
            .filter(([tokenId, token]) => {
                // Only include tokens with valid addresses and active chains
                return CHAINS[selectedChain].active && isValidTokenAddress(token.address);
            })
            .map(([tokenId, token]) => ({
                ...token,
                id: `${selectedChain}_${tokenId}`,
                chainId: selectedChain,
                chainName: CHAINS[selectedChain].name,
                chainIcon: CHAINS[selectedChain].icon,
                chainColor: CHAINS[selectedChain].color,
                chainActive: CHAINS[selectedChain].active
            }))
        : []; // Show empty list when no chain is selected to prevent duplicates

    // Filter tokens based on search term.
    const filteredTokens = tokensToDisplay.filter(token => {
        const matchesSearch = token.symbol.toLowerCase().includes(tokenSearchTerm.toLowerCase()) ||
                             token.name.toLowerCase().includes(tokenSearchTerm.toLowerCase());
        return matchesSearch;
    });

    // Handle token selection - select token and close modal
    const handleTokenSelect = (tokenId) => {
        if (!selectedChain) {
            // Can't select token without chain
            return;
        }
        setSelectedToken(tokenId);
        const tokenData = CHAINS[selectedChain].tokens[tokenId];
        if (!tokenData) {
            return;
        }
        const fullTokenData = {
            ...tokenData,
            chainId: selectedChain,
            chainName: CHAINS[selectedChain].name,
            id: `${selectedChain}_${tokenId}`,
        };
        
        // Notify parent about token selection
        if (onTokenSelect) {
            onTokenSelect(fullTokenData);
        }
        
        // Close modal after token is selected
        setTimeout(() => {
            if (onClose) {
                onClose();
            }
        }, 100);
    };

    useEffect(() => {
        if (isOpen) {
            if (soleChainId) {
                setSelectedChain(selectedChainId || soleChainId);
            } else {
                setSelectedChain(selectedChainId);
            }
            setSelectedToken(selectedTokenId);
            setTokenSearchTerm('');
        }
    }, [isOpen, selectedChainId, selectedTokenId, soleChainId]);

    if (!isOpen) return null;

    return (
        <div className="token-selector-overlay" onClick={onClose}>
            <div
                className={`token-selector-modal${singleChainMode ? ' token-selector-modal-single-chain' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="token-selector-header">
                    <h2 className="token-selector-title">{title}</h2>
                    <button 
                        className="token-selector-close"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <div
                    className={`token-selector-content-wrapper${singleChainMode ? ' token-selector-content-wrapper-single' : ''}`}
                >
                    <div className={`token-selection-panel${singleChainMode ? ' token-selection-panel-single' : ''}`}>
                        <div className="panel-header">
                            <span>Select token</span>
                        </div>
                        
                        {/* Token Search Bar */}
                        <div className="panel-search">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search tokens"
                                value={tokenSearchTerm}
                                onChange={(e) => setTokenSearchTerm(e.target.value)}
                                disabled={!selectedChain}
                            />
                        </div>

                        {/* Token List */}
                        <div className="panel-content">
                            <div className="token-list">
                                {filteredTokens.map(token => {
                                    const tokenId = token.id.split('_')[1];
                                    const isSelected = selectedToken === tokenId;
                                    return (
                                    <div
                                        key={token.id}
                                        className={`token-option ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleTokenSelect(tokenId)}
                                    >
                                        <div className="token-icon" style={{ color: token.color }}>
                                            {token.icon.startsWith('/') ? (
                                                <img src={token.icon} alt={token.symbol} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            ) : (
                                                <span style={{ fontSize: '1.25rem' }}>{token.icon}</span>
                                            )}
                                        </div>
                                        <div className="token-info">
                                            <div className="token-main">
                                                <span className="token-symbol">{showConfidential ? `C${token.symbol}` : token.symbol}</span>
                                                <span className="token-name">{showConfidential ? `Confidential ${token.name}` : token.name}</span>
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <div className="selected-indicator">
                                                <i className="fas fa-check"></i>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>

                            {/* No tokens found or no chain selected */}
                            {filteredTokens.length === 0 && (
                                <div className="no-results">
                                    <i className="fas fa-search"></i>
                                    <p>No tokens found</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TokenSelector;
